export interface LivePeer {
  pc: RTCPeerConnection;
  channel: RTCDataChannel;
  sender: RTCRtpSender;
  mic: MediaStream;
  audio: HTMLAudioElement;
}

export interface LivePeerHandlers {
  onEvent: (event: any) => void;
  onLost: () => void;
  onConnected: () => void;
  onOpen: () => void;
}

export async function openMic(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({ audio: true });
}

export function openLivePeer({ onEvent, onLost, onConnected, onOpen }: LivePeerHandlers): LivePeer {
  const pc = new RTCPeerConnection();
  const audio = new Audio();
  audio.autoplay = true;
  pc.addEventListener("track", (event) => {
    audio.srcObject = new MediaStream([event.track]);
    void audio.play().catch(() => undefined);
  });
  pc.addEventListener("connectionstatechange", () => {
    if (pc.connectionState === "connected") onConnected();
    if (pc.connectionState === "failed") onLost();
  });
  const { sender } = pc.addTransceiver("audio", { direction: "sendrecv" });
  const channel = pc.createDataChannel("oai-events");
  channel.addEventListener("message", ({ data }) => {
    try {
      onEvent(JSON.parse(data));
    } catch {}
  });
  channel.addEventListener("open", onOpen);
  channel.addEventListener("close", onLost);
  return { pc, channel, sender, mic: new MediaStream(), audio };
}

export async function attachMic(peer: LivePeer, mic: MediaStream, muted: boolean): Promise<void> {
  peer.mic = mic;
  setMicMuted(peer, muted);
  await peer.sender.replaceTrack(mic.getAudioTracks()[0] ?? null);
}

export async function createOfferSdp(pc: RTCPeerConnection, iceTimeoutMs: number): Promise<string> {
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  if (pc.iceGatheringState !== "complete") {
    await new Promise<void>((resolve, reject) => {
      const onState = () => {
        if (pc.iceGatheringState !== "complete") return;
        clearTimeout(timeout);
        pc.removeEventListener("icegatheringstatechange", onState);
        resolve();
      };
      const timeout = setTimeout(() => {
        pc.removeEventListener("icegatheringstatechange", onState);
        reject(new Error("ice gathering timed out"));
      }, iceTimeoutMs);
      pc.addEventListener("icegatheringstatechange", onState);
      onState();
    });
  }
  const sdp = pc.localDescription?.sdp;
  if (!sdp) throw new Error("missing local sdp");
  return sdp;
}

export function setMicMuted(peer: LivePeer, muted: boolean): void {
  peer.mic.getAudioTracks().forEach((track) => {
    track.enabled = !muted;
  });
}

export function closeLivePeer(peer: LivePeer): void {
  peer.mic.getTracks().forEach((track) => track.stop());
  try {
    peer.channel.close();
  } catch {}
  peer.pc.close();
  peer.audio.srcObject = null;
}
